import { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import {
  DELIVERY_NOTE_DEFAULT_COLUMN_NAMES,
  DELIVERY_NOTE_FORM_LABELS,
  DELIVERY_NOTE_PAGE_ATTRIBUTE,
  DELIVERY_NOTE_PREVIEW_NO_COLUMN,
  DELIVERY_NOTE_TOTAL_HT,
} from "constants/forms";
import {
  buildDeliveryNoteLineHeaders,
  computeLineAmount,
  formatDeliveryNoteAmount,
  hasLinkedColumn,
  paginateDeliveryNoteLines,
  readLineCell,
} from "utils/deliveryNoteHelper";
import DeliveryNotePreviewColumn from "./DeliveryNotePreviewColumn";

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;
const PAGE_BOTTOM_RESERVE = 24;
const FIRST_PAGE_ONLY = [[]];
const EMPTY_CELL = "—";

const measure = (node, name) =>
  node.querySelector(`[data-measure="${name}"]`)?.offsetHeight || 0;

const hasSamePages = (pages, nextPages) =>
  pages.length === nextPages.length &&
  pages.every((page, index) => page.length === nextPages[index].length);

const DeliveryNoteTemplatePreview = ({
  snapshot,
  resolveImage,
  showTitle = true,
  showPlaceholders = true,
  note,
  scaleToContainer = true,
}) => {
  const intl = useIntl();
  const columnRef = useRef(null);
  const measureRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pages, setPages] = useState(FIRST_PAGE_ONLY);
  const lines = note?.lines || [];

  useLayoutEffect(() => {
    const column = columnRef.current;
    if (!column || !scaleToContainer) return undefined;
    const applyWidth = (width) => {
      if (width > 0) setScale(width / A4_WIDTH);
    };
    applyWidth(column.clientWidth);
    const observer = new ResizeObserver(([entry]) =>
      applyWidth(entry.contentRect.width)
    );
    observer.observe(column);
    return () => observer.disconnect();
  }, [scaleToContainer]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const node = measureRef.current;
    if (!node) return;
    const { paddingTop, paddingBottom } = getComputedStyle(node);
    const table = node.querySelector('[data-measure="table"]');
    const nextPages = paginateDeliveryNoteLines({
      rowHeights: Array.from(
        table?.tBodies[0]?.rows ?? [],
        (row) => row.offsetHeight
      ),
      headFirstHeight: measure(node, "head-first"),
      headNextHeight: measure(node, "head-next"),
      tableHeaderHeight: table?.tHead?.offsetHeight || 0,
      footerHeight: measure(node, "footer"),
      availableHeight:
        A4_HEIGHT -
        Number.parseFloat(paddingTop) -
        Number.parseFloat(paddingBottom) -
        PAGE_BOTTOM_RESERVE,
    });
    setPages((current) =>
      hasSamePages(current, nextPages) ? current : nextPages
    );
  });

  const headers = buildDeliveryNoteLineHeaders(snapshot.columns);

  const metaPlaceholder = showPlaceholders ? EMPTY_CELL : null;

  const renderMetaCell = (id, defaultMessage, value) => (
    <div className="delivery-note-preview__meta-cell" key={id}>
      <span className="delivery-note-preview__meta-label">
        <FormattedMessage id={id} defaultMessage={defaultMessage} />
      </span>
      <span className="delivery-note-preview__meta-value">
        {value || metaPlaceholder}
      </span>
    </div>
  );

  const formatAmount = (amount) =>
    formatDeliveryNoteAmount({
      intl,
      amount,
      currencySymbol: note?.currencySymbol,
    });

  const renderHeaderCell = (header) => (
    <th
      key={`delivery-note-preview-header-${header.position}-${header.field}`}
      className={
        header.field === "total" ? "delivery-note-preview__total" : undefined
      }
    >
      {header.field === "total" ? (
        <FormattedMessage {...DELIVERY_NOTE_DEFAULT_COLUMN_NAMES.total} />
      ) : (
        header.label
      )}
    </th>
  );

  const renderLineCell = (line, header) => {
    const cell = readLineCell(line, header.position);
    const key = `delivery-note-preview-cell-${header.position}-${header.field}`;

    if (header.field === "total") {
      return (
        <td key={key} className="delivery-note-preview__amount">
          {formatAmount(
            computeLineAmount({
              column: snapshot.columns[header.position],
              cell,
            })
          )}
        </td>
      );
    }

    const value = cell[header.field];

    return <td key={key}>{(value ?? "") === "" ? EMPTY_CELL : value}</td>;
  };

  const renderPlaceholderRow = () => (
    <tr>
      {headers.map((header) => (
        <td
          key={`delivery-note-preview-cell-${header.position}-${header.field}`}
        >
          {EMPTY_CELL}
        </td>
      ))}
    </tr>
  );

  const renderLineRow = (line, index) => (
    <tr key={line.key || `delivery-note-preview-line-${index}`}>
      {headers.map((header) => renderLineCell(line, header))}
    </tr>
  );

  const renderTable = (rowIndexes) => {
    const drawnRows = rowIndexes.filter((index) => lines[index]);

    return (
      <table className="delivery-note-preview__table">
        <thead>
          <tr>{headers.map(renderHeaderCell)}</tr>
        </thead>
        <tbody>
          {drawnRows.length
            ? drawnRows.map((index) => renderLineRow(lines[index], index))
            : renderPlaceholderRow()}
        </tbody>
      </table>
    );
  };

  const renderEmptyTable = () => (
    <div className="delivery-note-preview__empty-table">
      <FormattedMessage {...DELIVERY_NOTE_PREVIEW_NO_COLUMN} />
    </div>
  );

  const renderSignature = (name, signature) => (
    <div className="delivery-note-preview__signature">
      <span className="delivery-note-preview__signature-name">{name}</span>
      <div className="delivery-note-preview__signature-box">
        {signature && <img src={signature} alt="" />}
      </div>
    </div>
  );

  const renderLogo = (logo) => {
    const image = logo ? (
      <img src={logo} alt="" className="delivery-note-preview__logo" />
    ) : null;

    if (!showPlaceholders)
      return <span className="delivery-note-preview__logo-slot">{image}</span>;

    return (
      image || (
        <span className="delivery-note-preview__no-logo">
          <FormattedMessage
            id="delivery_note_preview_no_logo"
            defaultMessage="Pas de logo"
          />
        </span>
      )
    );
  };

  const renderParty = (party, fallbackId, fallbackMessage) => {
    const logo = resolveImage(snapshot[`${party}_logo_file_id`]);
    const name = snapshot[`${party}_name`];
    const address = snapshot[`${party}_address`]?.trim();
    const siret = snapshot[`${party}_siret`]?.trim();

    return (
      <div className="delivery-note-preview__party">
        {renderLogo(logo)}
        <span className="delivery-note-preview__party-name">
          {name || (
            <FormattedMessage
              id={fallbackId}
              defaultMessage={fallbackMessage}
            />
          )}
        </span>
        {address && (
          <span className="delivery-note-preview__party-address">
            {address}
          </span>
        )}
        {siret && (
          <span className="delivery-note-preview__party-siret">
            <FormattedMessage
              id="delivery_note_preview_siret"
              defaultMessage="SIRET {siret}"
              values={{ siret }}
            />
          </span>
        )}
      </div>
    );
  };

  const renderPartiesHeader = () => (
    <header className="delivery-note-preview__header">
      {renderParty("issuer", "delivery_note_issuer_block", "Émetteur")}
      {renderParty(
        "recipient",
        "delivery_note_recipient_block",
        "Destinataire"
      )}
    </header>
  );

  const renderNumber = () =>
    note?.number ? (
      <FormattedMessage
        id="delivery_note_preview_number_value"
        defaultMessage="N°BL - {number}"
        values={{ number: note.number }}
      />
    ) : (
      <FormattedMessage
        id="delivery_note_preview_number"
        defaultMessage="N°BL - ######"
      />
    );

  const renderTitles = () => (
    <div className="delivery-note-preview__titles">
      <p className="delivery-note-preview__subtitle">
        {snapshot.subtitle || (
          <FormattedMessage {...DELIVERY_NOTE_FORM_LABELS.subtitle} />
        )}
      </p>
      <h4 className="delivery-note-preview__title">
        {snapshot.title || (
          <FormattedMessage {...DELIVERY_NOTE_FORM_LABELS.title} />
        )}
      </h4>
      <p className="delivery-note-preview__number">{renderNumber()}</p>
    </div>
  );

  const renderContinuationHeader = () => (
    <p className="delivery-note-preview__continuation">{renderNumber()}</p>
  );

  const renderMeta = () => (
    <div className="delivery-note-preview__meta">
      {renderMetaCell(
        "delivery_note_preview_order_number",
        "N° de commande",
        note?.orderNumber
      )}
      {renderMetaCell(
        "delivery_note_preview_delivery_date",
        "Date de remise",
        note?.deliveryDate
      )}
    </div>
  );

  const renderFinancialTitle = () => (
    <p className="delivery-note-preview__section-title">
      <FormattedMessage
        id="delivery_note_preview_financial_info"
        defaultMessage="Informations financières"
      />
    </p>
  );

  const renderDocumentFooter = () => (
    <>
      {note?.total != null && hasLinkedColumn(snapshot.columns) && (
        <p className="delivery-note-preview__grand-total">
          <FormattedMessage
            {...DELIVERY_NOTE_TOTAL_HT}
            values={{ total: formatAmount(note.total) }}
          />
        </p>
      )}

      <p className="delivery-note-preview__section-title">
        <FormattedMessage
          id="delivery_note_preview_reserves"
          defaultMessage="Si réserves, les mentionner ici :"
        />
      </p>
      <div className="delivery-note-preview__reserves" />

      <footer className="delivery-note-preview__footer">
        <p className="delivery-note-preview__section-title delivery-note-preview__section-title--center">
          <FormattedMessage
            id="delivery_note_preview_signatures"
            defaultMessage="Signatures"
          />
        </p>
        <div className="delivery-note-preview__signatures">
          {renderSignature(
            snapshot.issuer_signatory_name,
            resolveImage(snapshot.issuer_signature_file_id)
          )}
          {renderSignature(
            snapshot.recipient_signatory_name,
            resolveImage(snapshot.recipient_signature_file_id)
          )}
        </div>
        <p className="delivery-note-preview__billing">
          <FormattedMessage
            id="delivery_note_preview_billing_mention"
            defaultMessage="Bon pour facturation et règlement"
          />
        </p>
      </footer>
    </>
  );

  const renderFirstPageHead = (withFinancialTitle) => (
    <>
      {renderPartiesHeader()}
      {renderTitles()}
      {renderMeta()}
      {withFinancialTitle && renderFinancialTitle()}
    </>
  );

  const renderSheet = (rowIndexes, pageIndex) => {
    const isLastPage = pageIndex === pages.length - 1;
    const showTable = rowIndexes.length > 0 || pages.length === 1;

    return (
      <div
        className="delivery-note-preview-page"
        key={`delivery-note-preview-page-${pageIndex}`}
        style={{ height: Math.ceil(A4_HEIGHT * scale) }}
      >
        <div
          className="delivery-note-preview"
          {...{ [DELIVERY_NOTE_PAGE_ATTRIBUTE]: pageIndex + 1 }}
          style={{
            height: A4_HEIGHT,
            transform: `scale(${scale})`,
            width: A4_WIDTH,
          }}
        >
          {pageIndex === 0
            ? renderFirstPageHead(showTable)
            : renderContinuationHeader()}
          {showTable &&
            (headers.length ? renderTable(rowIndexes) : renderEmptyTable())}
          {isLastPage && renderDocumentFooter()}
          {(!showPlaceholders || pages.length > 1) && (
            <p className="delivery-note-preview__page-number">
              <FormattedMessage
                id="delivery_note_preview_page_number"
                defaultMessage="{current}/{total}"
                values={{ current: pageIndex + 1, total: pages.length }}
              />
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderMeasureSheet = () => (
    <div
      aria-hidden="true"
      className="delivery-note-preview delivery-note-preview--measure"
      ref={measureRef}
      style={{ width: A4_WIDTH }}
    >
      <div data-measure="head-first">{renderFirstPageHead(true)}</div>
      <div data-measure="head-next">{renderContinuationHeader()}</div>
      {headers.length > 0 && (
        <table className="delivery-note-preview__table" data-measure="table">
          <thead>
            <tr>{headers.map(renderHeaderCell)}</tr>
          </thead>
          <tbody>{lines.map(renderLineRow)}</tbody>
        </table>
      )}
      <div data-measure="footer">{renderDocumentFooter()}</div>
    </div>
  );

  return (
    <DeliveryNotePreviewColumn columnRef={columnRef} showTitle={showTitle}>
      {pages.map(renderSheet)}
      {renderMeasureSheet()}
    </DeliveryNotePreviewColumn>
  );
};

DeliveryNoteTemplatePreview.propTypes = {
  snapshot: PropTypes.object.isRequired,
  resolveImage: PropTypes.func.isRequired,
  showTitle: PropTypes.bool,
  showPlaceholders: PropTypes.bool,
  note: PropTypes.object,
  scaleToContainer: PropTypes.bool,
};

export default DeliveryNoteTemplatePreview;
